/**
 * Document Confirm Gating — shared, pure logic.
 *
 * An order may only be promoted to `confirmed` when the billing party's
 * required billing documentation already exists, so a confirmed order is
 * immediately billable. Requirements come from the per-customer flags on
 * `customers` — the same definitions the Dashboard "Unbillable Orders" tab
 * uses, so confirm-time and delivered-time never drift.
 *
 * Scope: all tenants, including Production.
 */

export interface ConfirmDocCustomer {
  organization_name?: string | null;
  requires_tender_id?: boolean | null;
  requires_bol?: boolean | null;
  requires_tender?: boolean | null;
}

export interface ConfirmDocInput {
  /** Billing party of the order; `null`/`undefined` ⇒ never gated. */
  customer: ConfirmDocCustomer | null | undefined;
  /** `orders.tender_id` value. */
  tenderId?: string | null;
  /** At least one row in `order_bol_images` for the order. */
  hasBolImage: boolean;
  /** At least one row in `order_tender_images` for the order. */
  hasTenderImage: boolean;
  /**
   * True only when every leg's trailer resolves to `trailers.load_status = 'empty'`.
   * Empty trailers have no BOL, so the BOL requirement is waived. Fails closed:
   * unknown/unassigned trailers leave this false and the BOL stays required.
   */
  allLegTrailersEmpty?: boolean;
  /**
   * Dispatcher-declared "Pending BOL" (`orders.pending_bol`). Multi-leg orders
   * with at least one empty-trailer leg may be confirmed before the BOL exists,
   * without an admin override. Callers must only pass `true` after re-checking
   * eligibility (see `useConfirmDocGate`) so a stale flag can't waive anything.
   */
  pendingBol?: boolean;
  /**
   * True when this is an API-created order (has an `external_order_id`) and at
   * least one leg's explicit planned load status (`legs.is_loaded`) contradicts
   * the physical `trailers.load_status` of that leg's trailer. Never set for
   * UI-created orders, unset load statuses, or unknown trailers.
   */
  loadStatusMismatch?: boolean;
}

export type MissingConfirmDoc = "Tender ID" | "BOL" | "Tender" | "Load status";

/** The load-status blocker is not a document — it has its own copy. */
export const LOAD_STATUS_MISMATCH: MissingConfirmDoc = "Load status";

export const LOAD_STATUS_MISMATCH_MESSAGE =
  "Load status of the order and the trailer are out of sync. Verify with the customer about the orders load status and update the order or trailer.";

/**
 * Per-leg input for the API-order load-status cross-check.
 * `isLoaded` is `legs.is_loaded`; `trailerLoadStatus` is `trailers.load_status`.
 */
export interface LegLoadStatusPair {
  isLoaded?: boolean | null;
  trailerLoadStatus?: string | null;
}

/**
 * True when ANY leg has both values definitively set and they disagree.
 * Unset (`null`/`undefined`) planned load status, or a trailer whose physical
 * status isn't a definite `loaded`/`empty`, never blocks.
 */
export function hasLoadStatusMismatch(legs: readonly LegLoadStatusPair[]): boolean {
  return legs.some((leg) => {
    if (typeof leg.isLoaded !== "boolean") return false;
    const physical = leg.trailerLoadStatus;
    if (physical !== "loaded" && physical !== "empty") return false;
    return (physical === "loaded") !== leg.isLoaded;
  });
}

/** Does the blocker list contain an actual missing document (vs. only the mismatch)? */
export function hasMissingDocuments(missing: readonly string[]): boolean {
  return missing.some((m) => m !== LOAD_STATUS_MISMATCH);
}

/**
 * Which of the billing party's required documents are still missing?
 * Returns `[]` when there is no billing party or it requires nothing.
 * The load-status mismatch is appended independently of the billing party.
 */
export function getMissingConfirmDocs(input: ConfirmDocInput): MissingConfirmDoc[] {
  const customer = input.customer;
  const missing: MissingConfirmDoc[] = [];
  if (customer) {
    if (customer.requires_tender_id && !(input.tenderId ?? "").trim()) {
      missing.push("Tender ID");
    }
    if (
      customer.requires_bol &&
      !input.hasBolImage &&
      !input.allLegTrailersEmpty &&
      !input.pendingBol
    ) {
      missing.push("BOL");
    }
    if (customer.requires_tender && !input.hasTenderImage) missing.push("Tender");
  }
  if (input.loadStatusMismatch) missing.push(LOAD_STATUS_MISMATCH);
  return missing;
}

/** Human sentence for the disabled-button tooltip and the rejection toast. */
export function formatMissingDocs(
  missing: readonly string[],
  customerName?: string | null,
): string {
  if (missing.length === 0) return "";
  const docs = missing.filter((m) => m !== LOAD_STATUS_MISMATCH);
  const parts: string[] = [];
  if (docs.length > 0) {
    const who = (customerName ?? "").trim();
    parts.push(
      `Missing billing documentation${who ? ` for ${who}` : ""}: ${docs.join(", ")}.`,
    );
  }
  if (missing.includes(LOAD_STATUS_MISMATCH)) parts.push(LOAD_STATUS_MISMATCH_MESSAGE);
  return parts.join(" ");
}


/** Sentinel thrown by the mutation pre-flight; encodes the missing labels. */
export const CONFIRM_REQUIRES_BILLING_DOCS = "CONFIRM_REQUIRES_BILLING_DOCS";

export function confirmDocsErrorMessage(missing: readonly string[]): string {
  return `${CONFIRM_REQUIRES_BILLING_DOCS}::${missing.join("|")}`;
}

export function parseConfirmDocsError(message: string): string[] | null {
  if (!message?.startsWith(CONFIRM_REQUIRES_BILLING_DOCS)) return null;
  const raw = message.split("::")[1] ?? "";
  return raw ? raw.split("|") : [];
}

/** Action hint appended to blocked copy; empty when only the load-status mismatch applies. */
export function confirmBlockActionHint(missing: readonly string[]): string {
  return hasMissingDocuments(missing) ? "Upload the required documentation before confirming." : "";
}
